import { useState, useEffect, useRef, useCallback } from 'react';
import { Device } from '../types/meeting.types';
import { attachStreamToVideo } from '../utils/mediaUtils.ts';
import {
  getVideoConstraints,
  type VideoQualityTier,
} from '../utils/videoQuality.ts';

export type { VideoQuality, VideoQualityTier } from '../utils/videoQuality.ts';
export { VIDEO_QUALITY_OPTIONS } from '../utils/videoQuality.ts';

export const useMediaStream = (videoQuality: VideoQualityTier) => {
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const localScreenVideoRef = useRef<HTMLVideoElement | null>(null);
  const localCameraPreviewRef = useRef<HTMLVideoElement | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const isInitializedRef = useRef(false);
  const initialQualityRef = useRef<VideoQualityTier>(videoQuality);
  
  const [availableCameras, setAvailableCameras] = useState<Device[]>([]);
  const [availableMicrophones, setAvailableMicrophones] = useState<Device[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('');
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [streamReady, setStreamReady] = useState(false);
  const [localCameraStream, setLocalCameraStream] = useState<MediaStream | null>(null);
  const [localScreenStream, setLocalScreenStream] = useState<MediaStream | null>(null);
  const previousVideoQualityRef = useRef<VideoQualityTier>(videoQuality);

  // Track değiştirme callback'i - WebRTC tarafından set edilecek
  const onTrackChangeRef = useRef<((track: MediaStreamTrack) => void) | null>(null);

  const setOnTrackChange = useCallback((callback: (track: MediaStreamTrack) => void) => {
    onTrackChangeRef.current = callback;
  }, []);

  const clearVideoElement = useCallback((ref: React.RefObject<HTMLVideoElement | null>) => {
    if (ref.current) {
      ref.current.srcObject = null;
    }
  }, []);

  const stopMediaStreamRef = useCallback((ref: React.MutableRefObject<MediaStream | null>) => {
    if (!ref.current) return;
    ref.current.getTracks().forEach((track) => track.stop());
    ref.current = null;
  }, []);

  /** Tüm kamera/mikrofon track'lerini ve video öğelerini serbest bırakır. */
  const stopStream = useCallback(() => {
    stopMediaStreamRef(localStreamRef);
    stopMediaStreamRef(screenStreamRef);
    stopMediaStreamRef(cameraStreamRef);
    clearVideoElement(localVideoRef);
    clearVideoElement(localScreenVideoRef);
    clearVideoElement(localCameraPreviewRef);
    setLocalCameraStream(null);
    setLocalScreenStream(null);
    setIsScreenSharing(false);
    isInitializedRef.current = false;
  }, [clearVideoElement, stopMediaStreamRef]);

  const syncLocalVideoElements = useCallback((screenSharing: boolean) => {
    if (screenSharing) {
      attachStreamToVideo(localScreenVideoRef.current, screenStreamRef.current);
      attachStreamToVideo(localCameraPreviewRef.current, cameraStreamRef.current);
      return;
    }

    attachStreamToVideo(localVideoRef.current, localStreamRef.current);
  }, []);

  const replaceLocalVideoTrack = useCallback((newVideoTrack: MediaStreamTrack) => {
    newVideoTrack.contentHint = 'motion';

    if (localStreamRef.current) {
      const oldVideoTrack = localStreamRef.current.getVideoTracks()[0];
      if (oldVideoTrack) {
        localStreamRef.current.removeTrack(oldVideoTrack);
        oldVideoTrack.stop();
      }
      localStreamRef.current.addTrack(newVideoTrack);
    }

    cameraStreamRef.current = localStreamRef.current?.clone() || null;
    setLocalCameraStream(cameraStreamRef.current);

    syncLocalVideoElements(isScreenSharing);

    if (onTrackChangeRef.current) {
      onTrackChangeRef.current(newVideoTrack);
    }
  }, [isScreenSharing, syncLocalVideoElements]);

  const refreshCameraTrack = useCallback(async (deviceId?: string) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: getVideoConstraints(videoQuality, deviceId || selectedCamera || undefined),
        audio: false
      });

      const newVideoTrack = stream.getVideoTracks()[0];
      if (!newVideoTrack) {
        return;
      }

      replaceLocalVideoTrack(newVideoTrack);

      if (deviceId) {
        setSelectedCamera(deviceId);
      }
    } catch (error) {
      console.error('Kamera kalitesi/degisimi hatası:', error);
    }
  }, [replaceLocalVideoTrack, selectedCamera, videoQuality]);

  useEffect(() => {
    if (!streamReady) return;

    let cancelled = false;
    let attempts = 0;

    const trySync = () => {
      if (cancelled || attempts > 30) return;
      attempts += 1;

      if (isScreenSharing) {
        if (!localScreenVideoRef.current || !screenStreamRef.current) {
          window.setTimeout(trySync, 50);
          return;
        }
      } else if (!localVideoRef.current || !localStreamRef.current) {
        window.setTimeout(trySync, 50);
        return;
      }

      syncLocalVideoElements(isScreenSharing);
    };

    trySync();

    return () => {
      cancelled = true;
    };
  }, [streamReady, isScreenSharing, syncLocalVideoElements]);

  useEffect(() => {
    const initializeMedia = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter(device => device.kind === 'videoinput');
        const microphones = devices.filter(device => device.kind === 'audioinput');
        
        setAvailableCameras(cameras.map((device, index) => ({
          deviceId: device.deviceId,
          label: device.label || `Kamera ${index + 1}`
        })));
        setAvailableMicrophones(microphones.map((device, index) => ({
          deviceId: device.deviceId,
          label: device.label || `Mikrofon ${index + 1}`
        })));

        const constraints: MediaStreamConstraints = {
          video: getVideoConstraints(initialQualityRef.current),
          audio: true
        };

        console.log('Requesting media permissions...');
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        console.log('Media stream obtained!', stream.getTracks());
        
        localStreamRef.current = stream;
        cameraStreamRef.current = stream.clone();
        setLocalCameraStream(cameraStreamRef.current);
        const initialVideoTrack = stream.getVideoTracks()[0];
        if (initialVideoTrack) {
          initialVideoTrack.contentHint = 'motion';
        }
        setStreamReady(true);
        isInitializedRef.current = true;

        const tryAttachStream = () => {
          if (localVideoRef.current) {
            attachStreamToVideo(localVideoRef.current, stream);
          } else {
            setTimeout(tryAttachStream, 100);
          }
        };
        tryAttachStream();

        if (cameras.length > 0) {
          setSelectedCamera(cameras[0].deviceId);
        }
        if (microphones.length > 0) {
          setSelectedMicrophone(microphones[0].deviceId);
        }
      } catch (error: any) {
        console.error('Kamera/mikrofon erişim hatası:', error);
        if (error.name === 'NotAllowedError') {
          alert('Kamera ve mikrofon erişimi reddedildi. Lütfen tarayıcı ayarlarından izin verin.');
        } else if (error.name === 'NotFoundError') {
          alert('Kamera veya mikrofon bulunamadı.');
        } else if (error.name === 'NotReadableError') {
          alert('Kamera veya mikrofon başka bir uygulama tarafından kullanılıyor.');
        } else {
          alert('Kamera/mikrofon hatası: ' + error.message);
        }
      }
    };

    initializeMedia();

    return () => {
      stopStream();
    };
  }, [stopStream]);

  useEffect(() => {
    if (!isInitializedRef.current || isScreenSharing || !localStreamRef.current) {
      return;
    }
    if (previousVideoQualityRef.current === videoQuality) {
      return;
    }
    previousVideoQualityRef.current = videoQuality;
    refreshCameraTrack();
  }, [isScreenSharing, refreshCameraTrack, videoQuality]);

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        const newState = !isVideoEnabled;
        videoTrack.enabled = newState;
        setIsVideoEnabled(newState);
        return newState;
      }
    }
    return isVideoEnabled;
  };

  const toggleAudio = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        const newState = !isAudioEnabled;
        audioTrack.enabled = newState;
        setIsAudioEnabled(newState);
        return newState;
      }
    }
    return isAudioEnabled;
  };

  const changeCamera = async (deviceId: string) => {
    if (localStreamRef.current) {
      await refreshCameraTrack(deviceId);
    }
  };

  const changeMicrophone = async (deviceId: string) => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.stop();
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: { deviceId: { exact: deviceId } }
        });

        const newAudioTrack = stream.getAudioTracks()[0];
        
        if (localStreamRef.current) {
          const oldAudioTrack = localStreamRef.current.getAudioTracks()[0];
          if (oldAudioTrack) {
            localStreamRef.current.removeTrack(oldAudioTrack);
          }
          localStreamRef.current.addTrack(newAudioTrack);
        }

        setSelectedMicrophone(deviceId);
      } catch (error) {
        console.error('Mikrofon değiştirme hatası:', error);
      }
    }
  };

  const toggleScreenShare = async (): Promise<boolean> => {
    try {
      if (isScreenSharing) {
        // Ekran paylaşımını kapat, kameraya dön
        console.log('Stopping screen share, switching back to camera');
        
        if (screenStreamRef.current) {
          screenStreamRef.current.getTracks().forEach(track => track.stop());
          screenStreamRef.current = null;
        }

        // Yeni kamera stream'i al
        const cameraStream = await navigator.mediaDevices.getUserMedia({
          video: getVideoConstraints(videoQuality, selectedCamera || undefined),
          audio: false
        });

        const cameraVideoTrack = cameraStream.getVideoTracks()[0];

        // Local stream'deki video track'i değiştir
        if (localStreamRef.current) {
          const oldVideoTrack = localStreamRef.current.getVideoTracks()[0];
          if (oldVideoTrack) {
            localStreamRef.current.removeTrack(oldVideoTrack);
            oldVideoTrack.stop();
          }
          localStreamRef.current.addTrack(cameraVideoTrack);
        }

        cameraStreamRef.current = cameraStream;
        setLocalCameraStream(cameraStream);
        setLocalScreenStream(null);

        setIsScreenSharing(false);

        // WebRTC'ye yeni track'i bildir
        if (onTrackChangeRef.current) {
          onTrackChangeRef.current(cameraVideoTrack);
        }

        return false;
      } else {
        // Ekran paylaşımını başlat
        console.log('Starting screen share');
        
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 30 }
          },
          audio: true
        });

        screenStreamRef.current = screenStream;
        setLocalScreenStream(screenStream);
        const screenVideoTrack = screenStream.getVideoTracks()[0];
        if (screenVideoTrack) {
          screenVideoTrack.contentHint = 'detail';
        }

        // Kamera track'ini ayri stream'de tut (WebRTC icin localStream'e ekran track'i eklenir)
        if (localStreamRef.current) {
          const oldVideoTrack = localStreamRef.current.getVideoTracks()[0];
          if (oldVideoTrack) {
            cameraStreamRef.current = new MediaStream([oldVideoTrack]);
            setLocalCameraStream(cameraStreamRef.current);
            localStreamRef.current.removeTrack(oldVideoTrack);
          }
          if (screenVideoTrack) {
            localStreamRef.current.addTrack(screenVideoTrack);
          }
        }

        setIsScreenSharing(true);

        // WebRTC'ye yeni track'i bildir
        if (onTrackChangeRef.current) {
          onTrackChangeRef.current(screenVideoTrack);
        }

        // Ekran paylaşımı kullanıcı tarafından durdurulursa
        screenVideoTrack.onended = async () => {
          console.log('Screen share ended by user');

          if (screenStreamRef.current) {
            screenStreamRef.current.getTracks().forEach(track => track.stop());
            screenStreamRef.current = null;
            setLocalScreenStream(null);
          }

          try {
            const preservedCameraTrack = cameraStreamRef.current?.getVideoTracks()[0];
            let cameraVideoTrack = preservedCameraTrack;

            if (!cameraVideoTrack || cameraVideoTrack.readyState !== 'live') {
              const cameraStream = await navigator.mediaDevices.getUserMedia({
                video: getVideoConstraints(videoQuality, selectedCamera || undefined),
                audio: false
              });
              cameraVideoTrack = cameraStream.getVideoTracks()[0];
              cameraStreamRef.current = cameraStream;
              setLocalCameraStream(cameraStream);
            }

            if (localStreamRef.current && cameraVideoTrack) {
              const oldVideoTrack = localStreamRef.current.getVideoTracks()[0];
              if (oldVideoTrack) {
                localStreamRef.current.removeTrack(oldVideoTrack);
              }
              localStreamRef.current.addTrack(cameraVideoTrack);
            }

            setIsScreenSharing(false);

            if (onTrackChangeRef.current) {
              onTrackChangeRef.current(cameraVideoTrack);
            }
          } catch (err) {
            console.error('Camera restore error:', err);
            setIsScreenSharing(false);
          }
        };

        return true;
      }
    } catch (error) {
      console.error('Ekran paylaşımı hatası:', error);
      if ((error as Error).name !== 'NotAllowedError') {
        alert('Ekran paylaşımı başlatılamadı.');
      }
      return isScreenSharing;
    }
  };

  return {
    localVideoRef,
    localScreenVideoRef,
    localCameraPreviewRef,
    localStreamRef,
    localCameraStream,
    localScreenStream,
    cameraStreamRef,
    screenStreamRef,
    availableCameras,
    availableMicrophones,
    selectedCamera,
    selectedMicrophone,
    isVideoEnabled,
    isAudioEnabled,
    isScreenSharing,
    toggleVideo,
    toggleAudio,
    changeCamera,
    changeMicrophone,
    toggleScreenShare,
    stopStream,
    setOnTrackChange
  };
};